import { useEffect } from "react";

import { useWorkspaceCopy } from "../../i18n/workspaceCatalog";
import { adminFeatureMessage, ADMIN_FEATURE_SOURCE_KEY, type AdminFeatureMessageKey } from "./adminFeatureCatalog";
import { adminSurfaceMessage, ADMIN_SURFACE_SOURCE_KEY, type AdminSurfaceMessageKey } from "./adminSurfaceCatalog";
import { ADMIN_LONG_COPY_KEYS, ADMIN_SURFACE_LONG_COPY } from "./adminSurfaceLongCopy";

/*
 * IMPORTANT AUTHORITY RULE
 *
 * This runtime never scans generic headings/buttons/labels/table headers. It is
 * limited to structural classes whose text is authored by Kavoosh source code.
 * User/server/record values rendered in ordinary heading, button, or table-header
 * nodes therefore cannot become translation input merely because their value
 * matches a catalog source string such as "Active" or "Organization".
 */
const SAFE_TEXT_SELECTOR = [
  ".admin-v12-page-heading > div > p",
  ".admin-v12-page-heading > div > h1",
  ".admin-v12-page-heading > div > span",
  ".admin-v12-page-heading > div:last-child > button",
  ".admin-v12-tabs > button",
  ".admin-v12-subtabs > button",
  ".admin-v12-eyebrow",
  ".admin-v12-kicker",
  ".admin-v12-empty > strong",
  ".admin-v12-empty > small",
  ".admin-v12-prerequisite > strong",
  ".admin-v12-privacy-note",
  ".admin-ops-boundary > p",
  ".admin-ops-boundary > h3",
  ".admin-ops-subroute-bar > span",
  ".admin-ops-subroute-bar > button",
  ".admin-ops-empty > strong",
  ".admin-ops-empty > span",
  ".data-imports-heading > div > p",
  ".data-imports-heading > div > h1",
  ".data-imports-heading > div > span",
  ".data-imports-heading > button",
  ".data-imports-active-heading > p",
  ".data-imports-active-heading > small",
  ".risk-heading > div > p",
  ".risk-heading > div > h1",
  ".risk-heading > div > span",
  ".risk-heading > div:last-child > button",
  ".risk-empty-state > strong",
  ".risk-empty-state > span",
  ".intel-heading > div > p",
  ".intel-heading > div > h1",
  ".intel-heading > div > span",
  ".intel-heading > div:last-child > button",
  ".intel-tabs > button",
  ".intel-boundary > strong",
  ".intel-section-heading > p",
  ".intel-section-heading > h2",
  ".intel-section-heading > span",
  ".content-heading > div > p",
  ".content-heading > div > h1",
  ".content-heading > div > span",
  ".content-heading > div:last-child > button",
  ".content-tabs > button",
  ".content-summary > div > span",
  ".policy-document-card > h2",
  ".policy-document-card > p",
  ".policy-history > h2",
].join(",");

const SAFE_ATTRIBUTE_SELECTOR = [
  ".admin-v12-page-heading [aria-label]",
  ".admin-v12-page-heading [title]",
  ".admin-v12-tabs [aria-label]",
  ".admin-v12-subtabs [aria-label]",
  ".admin-ops-subroute-bar [aria-label]",
  ".data-imports-heading [aria-label]",
  ".risk-heading [aria-label]",
  ".intel-heading [aria-label]",
  ".intel-tabs [aria-label]",
  ".content-heading [aria-label]",
  ".content-tabs [aria-label]",
].join(",");

const SKIP_SELECTOR = [
  "tbody", "option", "code", "pre", "textarea", "[contenteditable='true']", "[data-authoritative-content]",
  ".admin-v12-content-preview", ".admin-v12-rich-text", ".admin-v12-audit-payload",
  ".admin-v12-search-results", ".admin-v12-notification-list", ".admin-v12-notification-page-list",
  ".policy-preview-browser article",
].join(",");
const LONG_KEY_SET = new Set<AdminSurfaceMessageKey>(ADMIN_LONG_COPY_KEYS);

const originalText = new WeakMap<Text, string>();
const originalAttributes = new WeakMap<Element, Map<string, string>>();

function hasSource(source: string): boolean {
  const normalized = source.trim();
  return Boolean(ADMIN_FEATURE_SOURCE_KEY[normalized] || ADMIN_SURFACE_SOURCE_KEY[normalized]);
}

function surfaceTranslation(locale: ReturnType<typeof useWorkspaceCopy>["locale"], key: AdminSurfaceMessageKey): string {
  if (locale !== "en" && locale !== "fa" && locale !== "ar" && locale !== "it" && LONG_KEY_SET.has(key)) {
    return ADMIN_SURFACE_LONG_COPY[locale][key as keyof (typeof ADMIN_SURFACE_LONG_COPY)[typeof locale]];
  }
  return adminSurfaceMessage(locale, key);
}

function translated(locale: ReturnType<typeof useWorkspaceCopy>["locale"], source: string): string | null {
  const normalized = source.trim();
  const featureKey = ADMIN_FEATURE_SOURCE_KEY[normalized];
  if (featureKey) return adminFeatureMessage(locale, featureKey as AdminFeatureMessageKey);
  const surfaceKey = ADMIN_SURFACE_SOURCE_KEY[normalized];
  return surfaceKey ? surfaceTranslation(locale, surfaceKey as AdminSurfaceMessageKey) : null;
}

function replacePreservingWhitespace(source: string, value: string): string {
  const leading = source.match(/^\s*/)?.[0] ?? "";
  const trailing = source.match(/\s*$/)?.[0] ?? "";
  return `${leading}${value}${trailing}`;
}

function translateElementText(element: Element, locale: ReturnType<typeof useWorkspaceCopy>["locale"]): void {
  if (element.closest(SKIP_SELECTOR)) return;
  element.childNodes.forEach((node) => {
    if (node.nodeType !== Node.TEXT_NODE) return;
    const text = node as Text;
    let source = originalText.get(text);
    if (source === undefined) {
      const current = text.nodeValue ?? "";
      if (!hasSource(current)) return;
      source = current;
      originalText.set(text, source);
    }
    const value = translated(locale, source);
    if (value !== null) {
      const next = replacePreservingWhitespace(source, value);
      if (text.nodeValue !== next) text.nodeValue = next;
    }
  });
}

function translateElementAttributes(element: Element, locale: ReturnType<typeof useWorkspaceCopy>["locale"]): void {
  if (element.closest(SKIP_SELECTOR)) return;
  const originals = originalAttributes.get(element) ?? new Map<string, string>();
  for (const attribute of ["aria-label", "title"]) {
    const current = element.getAttribute(attribute);
    if (!current) continue;
    if (!originals.has(attribute) && hasSource(current)) originals.set(attribute, current);
    const source = originals.get(attribute);
    if (!source) continue;
    const value = translated(locale, source);
    if (value !== null && element.getAttribute(attribute) !== value) element.setAttribute(attribute, value);
  }
  if (originals.size) originalAttributes.set(element, originals);
}

export function applyAdminFeatureLocalization(root: ParentNode, locale: ReturnType<typeof useWorkspaceCopy>["locale"], path: string): void {
  if (!path.startsWith("/admin")) return;
  const shell = root.querySelector(".admin-v12-shell");
  if (!shell) return;
  shell.querySelectorAll(SAFE_TEXT_SELECTOR).forEach((element) => translateElementText(element, locale));
  shell.querySelectorAll(SAFE_ATTRIBUTE_SELECTOR).forEach((element) => translateElementAttributes(element, locale));
}

export function AdminFeatureLocalizationRuntime({ path }: { path: string }) {
  const { locale } = useWorkspaceCopy();
  useEffect(() => {
    if (!path.startsWith("/admin")) return;
    let scheduled = false;
    const apply = () => {
      scheduled = false;
      applyAdminFeatureLocalization(document, locale, path);
    };
    apply();
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      queueMicrotask(apply);
    });
    observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ["aria-label", "title"] });
    return () => observer.disconnect();
  }, [locale, path]);
  return null;
}
